import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ApiError, api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { LogEntry, Position } from "./types";
import { statusLabel, statusVariant } from "./flow";
import { LogTable } from "./log";

/** One person's run through an automation: where they are and every step so far. */
export function RunPage() {
  const { runId } = useParams({ from: "/automations/runs/$runId" });
  const id = Number(runId);
  const qc = useQueryClient();
  const [error, setError] = useState("");
  const q = useQuery({
    queryKey: ["automation-run", id],
    queryFn: () => api<{ position: Position; log: LogEntry[] }>(`/api/v1/automation-enrollments/${id}`),
  });
  const act = useMutation({
    mutationFn: (b: { action: string; json?: unknown }) => api(`/api/v1/automation-enrollments/${id}/${b.action}`, { json: b.json ?? {} }),
    onSuccess: () => {
      setError("");
      void qc.invalidateQueries({ queryKey: ["automation-run", id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  if (q.isLoading) return <Skeleton className="h-8 w-80" />;
  if (!q.data) return <p className="text-sm">Run not found.</p>;
  const p = q.data.position;
  const live = p.status === "active" || p.status === "waiting" || p.status === "paused";
  return (
    <div className="space-y-4">
      <div className="text-xs text-[var(--color-muted-foreground)]">
        <Link to="/automations/$automationId" params={{ automationId: String(p.automation_id) }} className="hover:underline">
          {p.automation_name}
        </Link>
      </div>
      <Card>
        <CardContent className="space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(p.status)}>{statusLabel(p.status)}</Badge>
            {p.shadow && <Badge variant="warning">shadow: logs only</Badge>}
            <span className="font-medium">{p.summary}</span>
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">
            Started {formatDateTime(p.entered_at)} by {p.started_by || "hand"} · version {p.version}
            {p.step_entered_at ? ` · at this step since ${formatDateTime(p.step_entered_at)}` : ""}
          </div>
          <div className="flex flex-wrap gap-1 pt-1">
            {(p.status === "active" || p.status === "waiting") && (
              <Button size="sm" variant="outline" onClick={() => act.mutate({ action: "pause" })}>
                Pause
              </Button>
            )}
            {p.status === "paused" && (
              <Button size="sm" variant="outline" onClick={() => act.mutate({ action: "resume" })}>
                Resume
              </Button>
            )}
            {p.status === "failed" && (
              <Button size="sm" variant="outline" onClick={() => act.mutate({ action: "move", json: { step: p.step_key } })}>
                Try the step again
              </Button>
            )}
            {live && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt("Take this person out of the automation? Reason (shown on their timeline):", "removed by hand");
                  if (reason !== null) act.mutate({ action: "exit", json: { reason } });
                }}
              >
                Take out
              </Button>
            )}
            <Link to="/contacts/$contactId" params={{ contactId: String(q.data.log[0]?.contact_id ?? "") }} className="ml-auto self-center text-xs text-[var(--color-primary)] hover:underline">
              Open contact
            </Link>
          </div>
          {error && <p className="text-xs text-[var(--color-danger-fg)]">{error}</p>}
        </CardContent>
      </Card>
      <LogTable entries={q.data.log} />
    </div>
  );
}
